import cn from 'classnames';
import React from 'react';
import Tooltip from '@dealroadshow/uikit-tooltip';
import byteConverter from '@framework/data-helpers/formatters/byteConverter';
import DataroomListItem from '../../../domain/DataroomListItem';
import { getApprovedCellTooltip } from './constants';
import UsersCell from './UsersCell';
import AccessRequestStatuses
  from '../../Set/ExploreSets/AccessRequestStatuses';
import StepsProgress
  from '../../Set/ExploreSets/StepsProgress';
import DataroomEmailAlerts from '../DataroomEmailAlerts';
import styles from '../datarooms.module.scss';
import LastActivityCell from './LastActivityCell';
import Actions from './Actions';
import hiddenStyles from '@dealroadshow/uikit-styles/helpers/hidden.module.scss';
import SectorCell from './SectorCell';
import NameCell from './NameCell';
import NameCellWithCountryCheck from './NameCellWithCountryCheck';
import IconCheck from '@dealroadshow/uikit-icon/IconCheck';
import AccessRequestStatusesWithCountryCheck
  from '../../Set/ExploreSets/AccessRequestStatuses/AccessRequestStatusesWithCountryCheck';
import Visibility from '@dataroom/file-indicators/FileVisibility/Visibility';

import TransactionTypeCell from './TransactionTypeCell';
import { ICellProps } from '@dealroadshow/uikit-table/DataTable';
import { IDataroomListItem } from '../../../domain/vo/DataroomListItem';

/**
 * @param {Object} cellProps
 * @param isDisabled
 */
export const cellNameCallback = (cellProps: ICellProps, isDisabled = false) =>
  <NameCell cellProps={ cellProps } isDisabled={ isDisabled } />;

export const cellNameNotClickableCallback = (cellProps: ICellProps) =>
  <NameCellWithCountryCheck cellProps={ cellProps } />;

export const cellStageCallback = (cellProps: ICellProps) => {
  const { stage } = cellProps.row;

  return (
    <Tooltip
      placement="bottom"
      content={ stage?.name }
      className={ styles.hideTooltipOnMobile }
    >
      <span className={ styles.statusIndicator } style={ { backgroundColor: stage?.color } } />
    </Tooltip>
  );
};

export const cellSectorCallback = (cellProps: ICellProps) => <SectorCell cellProps={ cellProps } />;

export const cellStatusIndicatorCallback = (cellProps: ICellProps, isDisabled = false) => {
  const { isDeactivated } = cellProps.row;
  const statusIndicatorCls = cn(
    styles.statusIndicator,
    isDeactivated ? styles.isExpired : styles.isPublished,
    { [styles.columnDisable]: isDisabled },
  );

  return (
    <Tooltip
      placement="bottom"
      content={ isDeactivated ? 'Deactivated' : 'Live' }
      className={ styles.hideTooltipOnMobile }
    >
      <span className={ statusIndicatorCls } />
    </Tooltip>
  );
};

export const cellUsersCallback = (cellProps: ICellProps, isDisabled = false) =>
  <UsersCell cellProps={ cellProps } isDisabled={ isDisabled } />;

export const cellCountFilesCallback = (cellProps: ICellProps, isDisabled = false) => {
  const {
    row,
    cellTitle,
    row: { filesCount },
  } = cellProps;
  const isDataroomDisabled = DataroomListItem.isDataroomDisabled(row);

  return (
    <span
      className={ cn(
        isDataroomDisabled && styles.deactivatedDataroom,
        isDisabled && styles.columnDisable,
      ) }
    >
      <span className={ styles.mobileColName }>{ cellTitle }</span>
      { filesCount?.toLocaleString() || '-' }
    </span>
  );
};

export const cellSizeCallback = (cellProps: ICellProps, isDisabled = false) => {
  const {
    row,
    row: { filesTotalSize },
    cellTitle,
  } = cellProps;
  const isDataroomDisabled = DataroomListItem.isDataroomDisabled(row);
  const size = byteConverter(filesTotalSize);

  return (
    <div>
      <span className={ styles.mobileColName }>{ cellTitle }</span>
      <span className={ cn(
        isDataroomDisabled && styles.deactivatedDataroom,
        isDisabled && styles.columnDisable,
      ) }
      >
        { size }
      </span>
    </div>
  );
};

export const cellEmailsCallback = (cellProps: ICellProps, isDisabled = false) => {
  const {
    row,
    cellTitle,
  } = cellProps;
  const isDataroomDisabled = DataroomListItem.isDataroomDisabled(row);

  const noActiveEmailAlertsCls = cn(
    styles.deactivatedDataroom,
    hiddenStyles.hiddenSm,
  );

  return (
    isDataroomDisabled ? (
      //  Render UI-cell for Deactivated Dataroom
      <div className={ noActiveEmailAlertsCls }> - </div>
    ) : (
      //  Render UI-cell for Active Dataroom
      <div className={ cn({ [styles.columnDisable]: isDisabled }) }>
        <span className={ styles.mobileColName }>{ cellTitle }</span>
        <DataroomEmailAlerts cellProps={ cellProps } />
      </div>
    )
  );
};

export const cellLastActivityCallback = (cellProps: ICellProps, isDisabled = false) => (
  <LastActivityCell cellProps={ cellProps } isDisabled={ isDisabled }>
    <Actions
      cellProps={ cellProps }
    />
  </LastActivityCell>
);

export const cellLastActivityExploreSetsCallback = (cellProps: ICellProps, isDisabled = false) => (
  <LastActivityCell cellProps={ cellProps } isDisabled={ isDisabled }>
    <AccessRequestStatuses
      cellProps={ cellProps }
    />
  </LastActivityCell>
);

export const cellLastActivityExploreSetsWithSessionCheckCallback = (cellProps: ICellProps, isDisabled = false) => (
  <LastActivityCell cellProps={ cellProps } isDisabled={ isDisabled }>
    <AccessRequestStatuses
      cellProps={ cellProps }
      withSessionCheck
    />
  </LastActivityCell>
);

export const cellLastActivityExploreSetsWithCountryCheckCallback = (cellProps: ICellProps, isDisabled = false) => (
  <LastActivityCell cellProps={ cellProps } isDisabled={ isDisabled }>
    <AccessRequestStatusesWithCountryCheck
      cellProps={ cellProps }
    />
  </LastActivityCell>
);

export const cellStepsToCompleteCallback = (cellProps: ICellProps) => (
  cellProps.cellData.steps
  && !Array.isArray(cellProps.cellData.steps)
  && (<StepsProgress stage={ cellProps.cellData } />)
);

export const getGroupsSummary = (dataroom: IDataroomListItem) => {
  const myPermissions = dataroom.myPermissions || '';
  const isDataroomDisabled = DataroomListItem.isDataroomDisabled(dataroom);
  const { isDeactivated } = dataroom;
  const isPending = dataroom?.accessRequest?.isPending;
  const isDenied = dataroom?.accessRequest?.isDenied;

  if (isDataroomDisabled) {
    return (
      <span className={ styles.deactivatedDataroom }>
        { isDeactivated && 'Deactivated' }
        { (isPending || isDenied) && myPermissions }
      </span>
    );
  }
  return myPermissions;
};

export const cellPermissionsCallback = (cellProps: ICellProps, isDisabled = false) => {
  const {
    row,
    cellTitle,
  } = cellProps;
  const permissionGroups = getGroupsSummary(row);

  return (
    <div className={ cn({
      [styles.columnDisable]: isDisabled,
    }) }
    >
      <span className={ styles.mobileColName }> { cellTitle } </span>
      { permissionGroups }
    </div>
  );
};

export const cellSponsorNameCallback = (cellProps: ICellProps, isDisabled = false) => {
  const {
    row,
    cellTitle,
  } = cellProps;
  const sponsorName = row.sponsor ? row.sponsor.name : '';

  return (
    <div className={ cn({ [styles.columnDisable]: isDisabled }) }>
      <span className={ styles.mobileColName }> { cellTitle } </span>
      <span className={ styles.mobileColValue }>{ sponsorName }</span>
    </div>
  );
};

export const cellUnderwriterNameCallback = ({
  row,
  cellTitle,
}: ICellProps, isDisabled = false, className?: string) => {
  const { underwriters } = row;
  const content = underwriters.map((item: { name: string }) => item.name)
    .join(', ');
  const isDataroomDisabled = DataroomListItem.isDataroomDisabled(row);

  return (
    <Tooltip
      placement="bottom"
      content={ content }
      className={ styles.hideTooltipOnMobile }
      disabled={ !content }
    >
      <div
        className={ cn(styles.underwritersWrapper, className, {
          [styles.deactivatedDataroom]: isDataroomDisabled,
          [styles.columnDisable]: isDisabled,
        }) }
      >
        <span className={ styles.mobileColName }>{ cellTitle }</span>
        <span className={ styles.cropTextCol }>{ content || '-' }</span>
      </div>
    </Tooltip>
  );
};

export const cellBankNameCallback = (cellProps: ICellProps, isDisabled = false) => {
  const underwriter = cellProps.row.underwriters?.[0]?.pivot;
  const bankDisplayName = underwriter?.displayName || '-';
  const truncatedBankName = bankDisplayName.length > 40 ? `${ bankDisplayName.slice(0, 40) }…` : bankDisplayName;

  return (
    <Tooltip
      placement="bottom"
      content={ bankDisplayName }
      className={ styles.hideTooltipOnMobile }
    >
      <span className={ cn(styles.cropTextBankName, {
        [styles.columnDisable]: isDisabled,
      }) }
      >
        { truncatedBankName }
      </span>
    </Tooltip>
  );
};

export const cellTransactionTypeCallback = (cellProps: ICellProps, isDisabled = false) =>
  <TransactionTypeCell cellProps={ cellProps } isDisabled={ isDisabled } />;

export const cellApprovedCallback = (cellProps: ICellProps, tenant: string, isDisabled = false) => {
  const { cellName } = cellProps;
  const { canFollow } = cellProps.row.allowedActions;

  return (
    <div className={ cn({ [styles.columnDisable]: isDisabled }) }>
      <span className={ styles.mobileColName }> { cellName } </span>
      { canFollow && (
        <Tooltip
          placement="bottom"
          content={ getApprovedCellTooltip(tenant) }
          className={ styles.hideTooltipOnMobile }
        >
          <IconCheck />
        </Tooltip>
      ) }
    </div>
  );
};

export const cellDeclarationCallback = (cellProps: ICellProps, isDisabled = false) => {
  const isDataroomDisabled = DataroomListItem.isDataroomDisabled(cellProps.row);

  return (
    <div className={ cn(styles.declarationCell, {
      [styles.columnDisable]: isDisabled,
      [styles.declarationCellDisabled]: isDataroomDisabled,
    }) }
    >
      <span className={ styles.mobileColName }>{ cellProps.cellTitle }</span>
      { cellProps.row.userDeclaration ? <Visibility value={ cellProps.row.userDeclaration } centered /> : '-' }
    </div>
  );
};
